/**
 * UI store slice for user interface state management
 * Replaces the existing ThemeProvider and adds global UI state management
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { UIStore, Notification, Theme } from '../types'
import { devtoolsDev } from '../middleware'

/**
 * Generates a unique ID for notifications
 */
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2)

/**
 * Initial UI state
 */
const initialUIState = {
  theme: 'system' as Theme,
  sidebarOpen: false,
  modalOpen: {},
  notifications: [],
  loading: {}
}

/**
 * Creates the UI store with actions
 */
const createUIStore = () => create<UIStore>()(
  devtoolsDev(
    // @ts-ignore
    persist(
      immer((set) => ({
        // Initial state
        ...initialUIState,

        // Theme actions
        // Note: This only stores the theme preference. The actual DOM class
        // manipulation is handled by ThemeProvider in contexts/theme-context.tsx
        // to avoid race conditions between multiple theme managers.
        setTheme: (theme: Theme) => {
          set((state) => {
            state.theme = theme
          })
        },

        // Sidebar actions
        toggleSidebar: () => {
          set((state) => {
            state.sidebarOpen = !state.sidebarOpen
          })
        },

        setSidebarOpen: (open: boolean) => {
          set((state) => {
            state.sidebarOpen = open
          })
        },

        // Modal actions
        openModal: (modalId: string) => {
          set((state) => {
            state.modalOpen[modalId] = true
          })
        },

        closeModal: (modalId: string) => {
          set((state) => {
            state.modalOpen[modalId] = false
          })
        },

        closeAllModals: () => {
          set((state) => {
            state.modalOpen = {}
          })
        },

        // Notification actions
        addNotification: (notification) => {
          const id = generateId()
          const newNotification: Notification = {
            ...notification,
            id,
            timestamp: Date.now(),
            duration: notification.duration || 5000
          }

          set((state) => {
            state.notifications.push(newNotification)
          })

          // Auto-remove notification after duration
          // @ts-ignore
          if (newNotification?.duration > 0) {
            setTimeout(() => {
              set((state) => {
                state.notifications = state.notifications.filter(n => n.id !== id)
              })
            }, newNotification?.duration)
          }
        },

        removeNotification: (id: string) => {
          set((state) => {
            state.notifications = state.notifications.filter(n => n.id !== id)
          })
        },

        clearNotifications: () => {
          set((state) => {
            state.notifications = []
          })
        },

        // Loading state actions
        setLoading: (key: string, loading: boolean) => {
          set((state) => {
            if (loading) {
              state.loading[key] = true
            } else {
              delete state.loading[key]
            }
          })
        },

        clearLoading: (key: string) => {
          set((state) => {
            delete state.loading[key]
          })
        }
      })),
      {
        name: 'taqat-ui-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          theme: state.theme,
          sidebarOpen: state.sidebarOpen
        })
      }
    ),
    'UIStore'
  )
)

/**
 * UI store instance
 */
export const uiStore = createUIStore()

/**
 * UI store hook for components
 * @returns UI store state and actions
 */
export const useUIStore = () => uiStore()

/**
 * Theme hook that mimics the original useTheme hook
 * Provides backward compatibility with existing components
 * @returns Theme context-like interface
 */
export const useTheme = () => {
  const { theme, setTheme } = useUIStore()
  
  return {
    theme,
    setTheme
  }
}

/**
 * Hook for sidebar state and actions
 * @returns Sidebar state and controls
 */
export const useSidebar = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore()
  
  return {
    sidebarOpen,
    toggleSidebar,
    setSidebarOpen
  }
}

/**
 * Hook for modal state and actions
 * @returns Modal state and controls
 */
export const useModal = (modalId?: string) => {
  const { modalOpen, openModal, closeModal, closeAllModals } = useUIStore()
  
  const isOpen = modalId ? modalOpen[modalId] || false : false
  
  return {
    isOpen,
    open: modalId ? () => openModal(modalId) : undefined,
    close: modalId ? () => closeModal(modalId) : undefined,
    closeAll: closeAllModals,
    modalStates: modalOpen
  }
}

/**
 * Hook for notifications state and actions
 * @returns Notifications state and controls
 */
export const useNotifications = () => {
  const { notifications, addNotification, removeNotification, clearNotifications } = useUIStore()
  
  return {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    // Convenience methods for different notification types
    success: (title: string, message?: string, duration?: number) => 
      addNotification({ type: 'success', title, message, duration }),
    error: (title: string, message?: string, duration?: number) => 
      addNotification({ type: 'error', title, message, duration }),
    warning: (title: string, message?: string, duration?: number) => 
      addNotification({ type: 'warning', title, message, duration }),
    info: (title: string, message?: string, duration?: number) => 
      addNotification({ type: 'info', title, message, duration })
  }
}

/**
 * Hook for loading state management
 * @param key - Optional specific loading key to track
 * @returns Loading state and controls
 */
export const useLoading = (key?: string) => {
  const { loading, setLoading, clearLoading } = useUIStore()
  
  const isLoading = key ? loading[key] || false : Object.keys(loading).length > 0
  
  return {
    isLoading,
    setLoading: key ? (state: boolean) => setLoading(key, state) : setLoading,
    clearLoading: key ? () => clearLoading(key) : clearLoading,
    loadingStates: loading
  }
}

/**
 * Selector hook for theme only
 * @returns Current theme
 */
export const useCurrentTheme = () => {
  return uiStore((state) => state.theme)
}

/**
 * Selector hook for notification count
 * @returns Number of active notifications
 */
export const useNotificationCount = () => {
  return uiStore((state) => state.notifications.length)
}

/**
 * Selector hook for global loading state
 * @returns Boolean indicating if any loading is active
 */
export const useGlobalLoading = () => {
  return uiStore((state) => Object.keys(state.loading).length > 0)
}